import * as ents from '@attain-apps/entities'
import { PartialId } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'

import * as display from '../../display'

import type * as models from './models'

type DisplayValueResolution =
  | {
      readonly type: 'apply'
      readonly value: string | undefined
    }
  | {
      readonly type: 'skip'
    }

/**
 * Resolves any visible node-card fields that should change alongside an edited
 * node value.
 *
 * This is owned by tree-editor state, not the node-panel component. Callers
 * pass the edited attribute model so reference bindings can resolve against the
 * attribute's declared dimension instead of guessing from arbitrary node data.
 */
export function getDisplayEditsForValueChange({
  attribute,
  bindings,
  partialId,
  referenceRecord,
  schema,
  value,
}: {
  readonly attribute?: ents.core.dms.models.Attribute
  readonly bindings?: ents.core.display.tree.Tree.DisplayBindings
  readonly partialId: PartialId
  readonly referenceRecord?: display.referenceRecords.models.Record
  readonly schema?: ents.core.dms.models.Schema
  readonly value: ents.core.dms.data.Value
}): models.DisplayEdits {
  const edits: models.DisplayEdits = {}

  for (const field of ents.core.display.tree.Tree.DisplayField.ALL) {
    const binding = bindings?.[field]
    if (binding?.attr !== partialId) {
      continue
    }

    const resolution = _resolveDisplayValue({
      attribute,
      binding,
      referenceRecord,
      schema,
      value,
    })

    if (resolution.type === 'skip') {
      continue
    }

    switch (field) {
      case 'label':
        edits.label = resolution.value ?? ''
        break
      case 'title':
        edits.title = resolution.value
        break
      case 'sublabel':
        edits.sublabel = resolution.value
        break
    }
  }

  return edits
}

/**
 * Resolves the reference-record target needed to build display edits for the
 * current value change when any matching binding depends on a breadcrumb.
 */
export function getReferenceTargetForValueChange({
  attribute,
  bindings,
  partialId,
  schema,
  value,
}: {
  readonly attribute?: ents.core.dms.models.Attribute
  readonly bindings?: ents.core.display.tree.Tree.DisplayBindings
  readonly partialId: PartialId
  readonly schema?: ents.core.dms.models.Schema
  readonly value: ents.core.dms.data.Value
}):
  | {
      entityId: ents.core.dms.models.Entity.Id
      recordId: ents.core.dms.data.Record.Id
    }
  | undefined {
  const hasReferenceBreadcrumbBinding =
    ents.core.display.tree.Tree.DisplayField.ALL.some(field => {
      const binding = bindings?.[field]
      return (
        binding?.attr === partialId && binding.source === 'reference-breadcrumb'
      )
    })

  if (!hasReferenceBreadcrumbBinding) {
    return undefined
  }

  return _getReferenceTarget({
    attribute,
    schema,
    value,
  })
}

/**
 * Resolves the text that should be written into a bound display field for the
 * current edited value.
 */
function _resolveDisplayValue({
  attribute,
  binding,
  referenceRecord,
  schema,
  value,
}: {
  readonly attribute?: ents.core.dms.models.Attribute
  readonly binding: ents.core.display.tree.Tree.DisplayBinding
  readonly referenceRecord?: display.referenceRecords.models.Record
  readonly schema?: ents.core.dms.models.Schema
  readonly value: ents.core.dms.data.Value
}): DisplayValueResolution {
  switch (binding.source) {
    case 'value-string':
      return TypeChecker.isString(value)
        ? { type: 'apply', value }
        : { type: 'skip' }
    case 'reference-breadcrumb':
      return _resolveReferenceDisplayValue({
        attribute,
        referenceRecord,
        schema,
        value,
      })
  }
}

/**
 * Resolves whether a reference-bound display field should be updated, cleared,
 * or left untouched when the current cache cannot provide a breadcrumb yet.
 */
function _resolveReferenceDisplayValue({
  attribute,
  referenceRecord,
  schema,
  value,
}: {
  readonly attribute?: ents.core.dms.models.Attribute
  readonly referenceRecord?: display.referenceRecords.models.Record
  readonly schema?: ents.core.dms.models.Schema
  readonly value: ents.core.dms.data.Value
}): DisplayValueResolution {
  if (
    ents.core.dms.data.Value.Reference.Default.isDefault(value) &&
    (!value.entityId || !value.recordId)
  ) {
    return { type: 'apply', value: undefined }
  }

  if (
    ents.core.dms.data.Value.Reference.InFormat.Dimension.isDimension(value)
  ) {
    const hasSelectedRecord = Object.values(value).some(
      ents.core.dms.data.Record.Id.isId,
    )

    if (!hasSelectedRecord) {
      return { type: 'apply', value: undefined }
    }
  }

  const referenceTarget = _getReferenceTarget({
    attribute,
    schema,
    value,
  })
  if (!referenceTarget || !referenceRecord) {
    return { type: 'skip' }
  }

  return {
    type: 'apply',
    value: display.referenceRecords.models.Record.toBreadcrumb(referenceRecord),
  }
}

/**
 * Extracts the referenced record target needed to build a breadcrumb label for
 * a bound display field.
 */
function _getReferenceTarget({
  attribute,
  schema,
  value,
}: {
  readonly attribute?: ents.core.dms.models.Attribute
  readonly schema?: ents.core.dms.models.Schema
  readonly value: ents.core.dms.data.Value
}):
  | {
      entityId: ents.core.dms.models.Entity.Id
      recordId: ents.core.dms.data.Record.Id
    }
  | undefined {
  if (
    !attribute ||
    !ents.core.dms.data.Value.Type.Reference.isReference(attribute.type)
  ) {
    return undefined
  }

  const { dimensionId } = attribute.type

  if (ents.core.dms.data.Value.Reference.Default.isDefault(value)) {
    const entityId = _getEntityId({
      dimensionId,
      entityId: value.entityId,
    })
    return entityId && value.recordId
      ? { entityId, recordId: value.recordId }
      : undefined
  }

  if (
    !schema ||
    !ents.core.dms.data.Value.Reference.InFormat.Dimension.isDimension(value)
  ) {
    return undefined
  }

  // Dimension-format references can carry multiple nested entity IDs. Follow
  // the same leaf-most selection rule as the reference input component so the
  // local chart label matches what the user just picked in the UI.
  const entities = schema.getEntitiesInDimension(dimensionId)
  for (let index = entities.length - 1; index >= 0; index--) {
    const entity = entities[index]
    const entityPartialId = ents.core.dms.models.Id.Parts.from(
      entity.id,
    ).partialId
    const recordId = value[entityPartialId]

    if (ents.core.dms.data.Record.Id.isId(recordId)) {
      return { entityId: entity.id, recordId }
    }
  }

  return undefined
}

/**
 * Normalizes a reference entity identifier to the full DMS entity ID used by
 * the reference-record store.
 */
function _getEntityId({
  dimensionId,
  entityId,
}: {
  readonly dimensionId: ents.core.dms.models.Dimension.Id
  readonly entityId?: ents.core.dms.models.Entity.Id | PartialId
}): ents.core.dms.models.Entity.Id | undefined {
  if (!entityId) {
    return undefined
  }

  if (ents.core.dms.models.Entity.Id.isId(entityId)) {
    return entityId
  }

  if (!PartialId.isPartialId(entityId)) {
    return undefined
  }

  return ents.core.dms.models.Entity.Id.from({
    context: ents.core.dms.models.Entity.Id.Context.from(dimensionId),
    partialId: entityId,
    type: ents.core.dms.models.Type.Entity,
  })
}
